import {
  Avatar,
  Button,
  Menu,
  NavGroup,
  NavItemContent,
  Sidebar,
  SwitcherButton,
  THEME_NAMES,
  type ThemeChoice,
  TopBar,
  UserButton,
  useTheme,
} from '@mogzo/ui';
import {
  BookOpen,
  CircleUserRound,
  ExternalLink,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  PanelLeft,
  SquareTerminal,
  Sun,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { DocsSearch } from '../docs/search';
import { authClient, PLAYER_SITE, type Project } from '../lib/api';
import { CrumbsProvider, useCrumbList } from '../lib/crumbs';
import { useDeveloper } from '../lib/developer';
import { useApi } from '../lib/use-api';
import { Brand } from './Brand';

const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon } as const;

/** Light, dark, or the same as the computer: in the top bar of every page. */
export function ThemeMenu() {
  const [theme, setTheme] = useTheme();
  const Icon = THEME_ICONS[theme];
  return (
    <Menu
      label="Theme"
      align="end"
      trigger={(props) => (
        <Button
          variant="ghost"
          size="sm"
          icon={Icon}
          label={`Theme: ${THEME_NAMES[theme]}`}
          {...props}
        />
      )}
      items={(['system', 'light', 'dark'] as ThemeChoice[]).map((choice) => ({
        label: THEME_NAMES[choice],
        icon: THEME_ICONS[choice],
        hint: choice === theme ? 'On' : undefined,
        onSelect: () => setTheme(choice),
      }))}
    />
  );
}

/** Inside a project: the current one, with the others to switch to. */
function ProjectSwitcher({ projectId }: { projectId: string }) {
  const list = useApi<{ projects: Project[] }>('/v1/dev/projects');
  const navigate = useNavigate();
  if (list.status !== 'ready') return null;
  const current = list.data.projects.find((project) => project.id === projectId);
  if (!current) return null;
  return (
    <Menu
      label="Projects"
      trigger={(props) => <SwitcherButton name={current.name} {...props} />}
      items={[
        ...list.data.projects.map((project) => ({
          label: project.name,
          hint: project.id === projectId ? 'Open' : undefined,
          onSelect: () => navigate(`/projects/${project.id}`),
        })),
        'separator' as const,
        { label: 'All projects', icon: LayoutGrid, onSelect: () => navigate('/') },
      ]}
    />
  );
}

function useCrumbLinks() {
  return useCrumbList().map((crumb, i, all) =>
    i === all.length - 1 || !crumb.to ? (
      <span key={crumb.label} aria-current={i === all.length - 1 ? 'page' : undefined}>
        {crumb.label}
      </span>
    ) : (
      <Link key={crumb.label} to={crumb.to}>
        {crumb.label}
      </Link>
    ),
  );
}

/** The dashboard frame from the M25 mockup: sidebar, top bar, then the page. */
export function Layout() {
  return (
    <CrumbsProvider>
      <Frame />
    </CrumbsProvider>
  );
}

function Frame() {
  const me = useDeveloper();
  const navigate = useNavigate();
  const crumbs = useCrumbLinks();
  const location = useLocation();
  const inProject = useMatch('/projects/:id/*');
  const projectId = inProject?.params.id !== 'new' ? inProject?.params.id : undefined;
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // The phone menu closes once a link in it is followed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every navigation
  useEffect(() => setMenuOpen(false), [location.pathname]);

  async function signOut() {
    setSigningOut(true);
    await authClient.signOut().catch(() => undefined);
    window.location.assign(PLAYER_SITE);
  }

  const here = location.pathname;
  const current = (match: boolean) => (match ? ('page' as const) : undefined);

  return (
    <div className="mz-shell" data-menu={menuOpen ? 'open' : undefined}>
      <Sidebar
        id="dashboard-menu"
        brand={<Brand />}
        switcher={projectId ? <ProjectSwitcher projectId={projectId} /> : undefined}
        footer={
          <Menu
            label="Account"
            placement="above"
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar name={me.displayName} size={32} />
                <div style={{ minWidth: 0 }}>
                  <div className="mz-user-name">{me.displayName}</div>
                  <div className="mz-user-email">{me.email}</div>
                </div>
              </div>
            }
            trigger={(props) => (
              <UserButton name={me.displayName} email={me.email} disabled={signingOut} {...props} />
            )}
            items={[
              { label: 'Account', icon: CircleUserRound, onSelect: () => navigate('/account') },
              { label: 'Open mogzo.com', icon: ExternalLink, href: PLAYER_SITE },
              'separator',
              { label: signingOut ? 'Signing out…' : 'Sign out', icon: LogOut, onSelect: signOut },
            ]}
          />
        }
      >
        <NavGroup>
          <Link
            to="/"
            className="mz-nav-item"
            aria-current={current(here === '/' || here.startsWith('/projects'))}
          >
            <NavItemContent icon={LayoutGrid}>Projects</NavItemContent>
          </Link>
          <Link
            to="/cli/sessions"
            className="mz-nav-item"
            aria-current={current(here.startsWith('/cli'))}
          >
            <NavItemContent icon={SquareTerminal}>CLI sessions</NavItemContent>
          </Link>
          <Link to="/docs" className="mz-nav-item" aria-current={current(here.startsWith('/docs'))}>
            <NavItemContent icon={BookOpen}>Docs</NavItemContent>
          </Link>
        </NavGroup>
        <NavGroup label="Account">
          <Link
            to="/account"
            className="mz-nav-item"
            aria-current={current(here.startsWith('/account'))}
          >
            <NavItemContent icon={CircleUserRound}>Account</NavItemContent>
          </Link>
        </NavGroup>
      </Sidebar>
      <button
        type="button"
        className="mz-drawer-scrim"
        aria-label="Close menu"
        onClick={() => setMenuOpen(false)}
      />

      <div className="mz-shell-main">
        <TopBar
          menuButton={
            <Button
              variant="ghost"
              icon={PanelLeft}
              label="Menu"
              className="mz-menu-button"
              aria-controls="dashboard-menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            />
          }
          breadcrumbs={crumbs}
          actions={
            <>
              {here.startsWith('/docs') && <DocsSearch />}
              <ThemeMenu />
            </>
          }
        />
        <Outlet />
      </div>
    </div>
  );
}
